import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const CalendarSettings: React.FC = () => {
  const { token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsConnected(response.data.connected);
    } catch (error) {
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API_URL}/google/auth`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open Google OAuth in new window
      window.location.href = response.data.authUrl;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to connect Google Calendar');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API_URL}/google/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsConnected(false);
      toast.success('Google Calendar disconnected');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to disconnect');
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <h1>📅 Google Calendar Integration</h1>
        <p>Sync your bookings automatically to Google Calendar</p>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {isConnected ? (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#10b981',
                color: 'white',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <span style={{ fontSize: '24px' }}>✅</span>
                <div>
                  <div style={{ fontWeight: '600' }}>Connected</div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    Your bookings will sync to Google Calendar automatically
                  </div>
                </div>
              </div>

              <h3 style={{ marginBottom: '16px' }}>Features Enabled:</h3>
              <ul style={{ lineHeight: '2', marginBottom: '24px' }}>
                <li>✅ Auto-sync new bookings to calendar</li>
                <li>✅ Update calendar when bookings change</li>
                <li>✅ Delete calendar events when bookings are cancelled</li>
                <li>✅ View upcoming bookings in dashboard</li>
              </ul>

              <button
                onClick={handleDisconnect}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Disconnect Google Calendar
              </button>
            </>
          ) : (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <span style={{ fontSize: '24px' }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: '600' }}>Not Connected</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Connect your Google account to enable calendar sync
                  </div>
                </div>
              </div>

              <h3 style={{ marginBottom: '16px' }}>Benefits:</h3>
              <ul style={{ lineHeight: '2', marginBottom: '24px' }}>
                <li>📅 Automatically sync bookings to your Google Calendar</li>
                <li>🔔 Get Google Calendar notifications for appointments</li>
                <li>📱 Access your schedule from any device</li>
                <li>👥 Share calendar with team members</li>
              </ul>

              <button
                onClick={handleConnect}
                disabled={connecting}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: connecting ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  opacity: connecting ? 0.6 : 1
                }}
              >
                {connecting ? '⏳ Connecting...' : '🔗 Connect Google Calendar'}
              </button>

              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                <strong>Note:</strong> You'll be redirected to Google to authorize access. 
                We only request permission to manage calendar events.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarSettings;
